import type { TranslationKeys } from './en';

export const zhCN: TranslationKeys = {
  common: {
    loading: '加载中...',
    error: '错误',
    success: '成功',
    cancel: '取消',
    confirm: '确认',
    delete: '删除',
    edit: '编辑',
    save: '保存',
    close: '关闭',
    logout: '登出',
    signOut: '退出登录',
    download: '下载',
    upload: '上传',
    search: '搜索',
    filter: '筛选',
    clear: '清除',
    remove: '移除',
    add: '添加',
    create: '创建',
    update: '更新',
    submit: '提交',
    back: '返回',
    next: '下一步',
    previous: '上一步',
    finish: '完成',
    retry: '重试',
    refresh: '刷新',
    copied: '已复制！',
    copyMessage: '复制消息',
    regenerate: '重新生成',
  },
  layout: {
    home: '首页',
    workspace: '工作区',
    chat: '对话',
    musicVideo: '音乐视频',
    wan22Video: 'Wan 2.2 视频',
    singingPhoto: '唱歌照片',
    randomSingingPhoto: '随机唱歌照片',
    virtualInfluencer: '虚拟网红',
    imageEdit: '图像编辑',
    faceSwap: '换脸',
    textToImage: '文本生成图像',
    nextScene: '下一场景',
    seedanceVideo: 'Seedance 2.0 视频',
    ltxVideo: 'LTX 视频',
    videoAddSound: '视频添加音效',
    ltxVideoExtend: 'LTX 视频续写',
    multiAngle: '多角度视图',
    humanPortrait: '人类肖像',
    models: '模型',
    history: '历史记录',
    generationHistory: '生成历史',
    organization: '组织',
    team: '团队',
    apiTokens: 'API 令牌',
    usage: '使用情况',
    billing: '账单',
    system: '系统',
    modelEditor: '模型编辑器',
    providerEditor: '提供商编辑器',
    orgEditor: '组织编辑器',
    myCredits: '我的积分',
    virtualInfluencers: '虚拟网红',
    influencerManager: '网红管理',
  },
  login: {
    title: 'Pipelet.ai',
    subtitle: '登录以继续',
    continueWithGoogle: '使用 Google 继续',
    continueWithGithub: '使用 GitHub 继续',
    signInWithPassword: '使用邮箱和密码登录',
    signInWithOAuth: '使用 Google 或 GitHub 登录',
    email: '邮箱',
    password: '密码',
    signIn: '登录',
    signingIn: '登录中...',
    or: '或',
    termsOfService: '服务条款',
    privacyPolicy: '隐私政策',
  },
  welcome: {
    greeting: '欢迎，{name}！',
    loading: '加载中...',
    getStarted: '通过创建新组织或加入现有组织来开始。',
    enterInviteCode: '输入组织提供的邀请码',
    createOrganization: '创建组织',
    joinOrganization: '加入组织',
    organizationName: '组织名称',
    chooseName: '为您的组织选择一个名称',
    inviteCode: '邀请码',
    creating: '创建中...',
    joining: '加入中...',
    create: '创建',
    join: '加入',
  },
  home: {
    title: '首页',
    tryAiModels: '尝试 AI 模型',
    exploreAiModels: '探索我们的 AI 模型集合，从文本生成到视频创作',
    categories: {
      workspace: {
        title: '工作区',
        description: 'LLM 驱动的媒体生成，带有聊天界面和媒体库',
      },
      chat: {
        title: '对话',
        description: '与 AI 模型实时聊天，支持流式响应',
      },
      image: {
        title: '图像',
        description: '使用 AI 生成、编辑和转换图像',
      },
      audio: {
        title: '音频',
        description: '文本转语音、语音转文本和音乐生成',
      },
      video: {
        title: '视频',
        description: '使用生成式 AI 创建和编辑视频',
      },
      virtualInfluencer: {
        title: '虚拟网红',
        description: '语音、视频和增强的多步骤工作流',
      },
      workflows: {
        title: '工作流',
        description: '用于复杂任务的多步骤 AI 管道',
      },
      data: {
        title: '数据',
        description: '嵌入、分类和数据处理',
      },
    },
  },
  team: {
    title: '团队',
    loading: '加载中...',
    inviteMembers: '邀请成员',
    generateSingleUseInvite: '生成单次使用邀请',
    generateMultiUseInvite: '生成多次使用邀请',
    inviteCodeLabel: '邀请码：',
    organizationMembers: '组织成员',
    member: '成员',
    admin: '管理员',
    remove: '移除',
    failedToLoadMembers: '加载成员失败',
    failedToGenerateInvite: '生成邀请失败',
    failedToUpdateRole: '更新角色失败',
    failedToRemoveMember: '移除成员失败',
  },
  chat: {
    title: '对话',
    selectModel: '选择模型',
    typeMessage: '输入您的消息...',
    send: '发送',
    thinking: '思考中',
    showThinking: '显示思考过程',
    hideThinking: '隐藏思考过程',
    copyMessage: '复制消息',
    regenerate: '重新生成',
    delete: '删除',
    copied: '已复制！',
    apiExamples: 'API 示例',
    failedToLoadModels: '加载可用模型失败',
    modelWarmingUp: '模型正在预热中。{seconds}秒后重试...',
    rateLimitExceeded: '超出速率限制。{seconds}秒后重试...',
    errorOccurred: '发生错误',
    startConversation: '选择一个模型并输入消息以开始对话',
  },
  workspace: {
    title: '工作区',
    library: '媒体库',
    clearWorkspace: '清空工作区',
    uploadMedia: '上传媒体',
    selectedMedia: '已选媒体',
    noMediaYet: '还没有媒体。添加文件以开始。',
    describeWhatYouWant: '描述您想要创建的内容...',
    usingSelectedItems: '使用 {count} 个选中的项目作为输入',
    selectMediaOrDescribe: '从媒体库中选择媒体或直接描述您想要的内容',
    startConversation: '与 AI 模型开始对话',
    selectModelAndType: '选择一个模型并描述您想要创建的内容',
    modelWarmingUp: '模型正在预热中...',
    modelWarmingUpDescription: '首次请求通常需要几分钟。模型正在加载到 GPU 上。',
    retryingIn: '{seconds}秒后重试',
    retryingNow: '正在重试...',
    takingLonger: '耗时较长',
    taskDetails: '任务详情',
    status: '状态',
    requestUrl: '请求 URL',
    requestPayload: '请求负载',
    close: '关闭',
  },
  musicVideo: {
    title: '音乐视频',
    searchGenerations: '搜索生成记录...',
    import: '导入',
    create: '创建',
    syncingJobStatus: '正在同步任务状态...',
    noGenerationsYet: '还没有音乐视频生成记录',
    getStarted: '创建您的第一个音乐视频以开始',
    completed: '已完成',
    processing: '处理中',
    queued: '排队中',
    failed: '失败',
    importVideo: '导入音乐视频',
    enterInstanceUuid: '输入实例 UUID',
    instanceUuidPlaceholder: '输入要导入的实例 UUID',
    cancel: '取消',
    importButton: '导入',
    importing: '导入中...',
    deleteConfirmation: '删除生成记录',
    deleteConfirmationMessage: '您确定要删除此生成记录吗？此操作无法撤销。',
    deleting: '删除中...',
    generateVideo: '生成视频',
    generateComplete: '完成',
    submittingRequest: '正在提交您的请求...',
    pleaseWait: '请稍候，我们正在处理您的音乐视频生成请求',
    generationStarted: '生成已成功启动！',
    generationStartedDescription: '您的音乐视频正在生成中。您可以查看进度并在完成后编辑视频。',
    backToList: '返回列表',
    viewGeneration: '查看生成记录',
  },
  randomSingingPhoto: {
    title: '随机唱歌照片',
    model: '模型：',
    orientation: '方向',
    portrait: '竖屏 (480x834)',
    landscape: '横屏 (834x480)',
    batchSize: '批量大小',
    duration: '时长',
    seconds: '秒',
    generateRandom: '生成随机唱歌照片',
    enterPromptOrRandom: '输入提示词（或留空表示"随机"）以生成图像，然后自动创建唱歌照片视频',
    describeImageStyle: '描述图像风格（或留空表示"随机"）...',
    submittingRequest: '正在提交请求...',
    generationInProgress: '生成进行中...',
    pressEnterToRegenerate: '按 Enter 键使用相同设置重新生成',
    pressEnterToGenerate: '按 Enter 键生成，Shift+Enter 换行。留空表示随机风格。',
    viewApiExamples: '查看 API 示例',
    startNewGeneration: '开始新生成',
    queued: '排队中',
    generatingImage: '正在生成图像',
    imageGenerationFailed: '图像生成失败',
    videoPlaybackError: '视频播放错误',
    imagePreview: '图像预览',
    videoQueued: '视频排队中',
    generatingVideo: '正在生成视频',
    creatingSingingPhoto: '正在从生成的图像创建唱歌照片视频...',
    imageGenerated: '图像已生成',
    selectDurationAndGenerate: '选择时长并点击生成视频',
    generateVideo: '生成视频',
    starting: '启动中...',
    regenerate: '重新生成',
    delete: '删除',
    justNow: '刚刚',
    minutesAgo: '{minutes}分钟前',
    hoursAgo: '{hours}小时前',
    daysAgo: '{days}天前',
    expired: '已过期',
    failed: '失败',
  },
  singingPhoto: {
    title: '唱歌照片',
    model: '模型：',
    duration: '时长',
    seconds: '秒',
    uploadImageRequired: '上传图像（必需）',
    clickToSelectOrDrag: '点击选择或拖放',
    describeVideo: '描述您想要生成的唱歌照片视频...',
    generationInProgress: '生成进行中...',
    pressEnterToRegenerate: '按 Enter 键使用相同设置重新生成',
    pressEnterToGenerate: '按 Enter 键生成，Shift+Enter 换行',
    viewApiExamples: '查看 API 示例',
    startNewGeneration: '开始新生成',
    queued: '排队中',
    generatingSingingPhoto: '正在生成唱歌照片视频',
    somethingWentWrong: '出现问题。请重试。',
    videoPlaybackError: '视频播放错误',
    selectFromHistoryOrStart: '从历史记录中选择生成或开始新的',
    justABitLonger: '再等一会儿...',
    regenerateWithSettings: '使用相同设置重新生成',
    delete: '删除',
    justNow: '刚刚',
    minutesAgo: '{minutes}分钟前',
    hoursAgo: '{hours}小时前',
    daysAgo: '{days}天前',
  },
  textToImage: {
    title: '文本生成图像',
    model: '模型：',
    width: '宽度',
    height: '高度',
    batchSize: '批量大小',
    nsfw: 'NSFW',
    describeImage: '描述您想要生成的图像...',
    jobInProgress: '任务进行中...',
    pressEnterToRegenerate: '按 Enter 键使用相同设置重新生成',
    pressEnterToGenerate: '按 Enter 键生成，Shift+Enter 换行',
    generationFailed: '生成失败',
    queued: '排队中',
    processing: '处理中...',
    generatingImages: '正在生成 {count} 张图像...',
    enterPromptToGenerate: '输入提示词以生成图像',
    viewApiExamples: '查看 API 示例',
    startNew: '开始新的',
    new: '新建',
    justNow: '刚刚',
    minutesAgo: '{minutes}分钟前',
    hoursAgo: '{hours}小时前',
    daysAgo: '{days}天前',
  },
  nextScene: {
    title: '下一场景生成器',
    uploadImage: '上传图片',
    dropImage: '将图片拖放到这里',
    dragDropOrClick: '拖放图片到这里，或点击选择',
    supportedFormats: '支持的格式：PNG、JPG、JPEG、WebP',
    removeImage: '移除图片',
    prompt: '描述下一场景',
    promptPlaceholder: '故事接下来会发生什么？描述场景...',
    generate: '生成下一场景',
    generating: '生成中...',
    new: '新建生成',
    results: '生成的场景',
    processing: '正在生成下一场景...',
    scene: '场景',
    noResults: '还没有生成场景',
  },
  history: {
    title: '生成历史',
    description: '查看和管理您的生成历史记录',
    table: {
      createdAt: '创建时间',
      model: '模型',
      endpoint: '类型',
      duration: '时长',
      credits: '积分',
      status: '状态',
    },
    status: {
      success: '成功',
      failed: '失败',
      pending: '等待中',
      processing: '处理中',
    },
    detail: {
      title: '生成详情',
      requestId: '请求 ID',
      model: '模型',
      endpoint: '端点',
      createdAt: '创建时间',
      status: '状态',
      credits: '使用积分',
      duration: '时长',
      metadata: '元数据',
      download: '下载',
      delete: '删除',
      noSelection: '从列表中选择一个生成记录以查看详情',
    },
    filters: {
      all: '全部',
      success: '成功',
      failed: '失败',
      model: '按模型筛选',
      status: '按状态筛选',
    },
    empty: '还没有生成历史',
    deleteConfirm: '确定要删除此生成记录吗？',
    deleteSuccess: '生成记录已成功删除',
    deleteFailed: '删除生成记录失败',
  },
  seedanceVideo: {
    title: 'Seedance 2.0 视频',
    description: '使用 Seedance 2.0 生成高质量视频。可上传首帧图片进行图生视频，或仅使用文字进行文生视频。',
    firstFrame: '首帧（可选）',
    lastFrame: '尾帧（可选）',
    dropImage: '将图片拖放到这里',
    dragDropOrClick: '拖放图片到这里，或点击选择',
    supportedFormats: '支持：PNG、JPG、JPEG、WebP',
    prompt: '提示词',
    promptPlaceholder: '描述您想要生成的视频...',
    settings: '设置',
    duration: '时长',
    ratio: '画面比例',
    resolution: '分辨率',
    generateAudio: '生成同步音频',
    generate: '生成视频',
    generating: '正在生成视频...',
    generatingInfo: '您的视频正在处理中，可以离开此页面，在历史记录中查看结果。',
    estimatedTime: '预计：约 {time} 秒',
    timeRemaining: '剩余：约 {time} 秒',
    submitted: '视频生成已开始！',
    submittedInfo: '视频正在异步处理中，请前往历史记录页面查看结果。',
    viewInHistory: '在历史记录中查看',
    newGeneration: '新建生成',
    historyTip: '提交后，您的视频将异步处理。请前往历史记录查看状态和下载。',
    goToHistory: '前往历史记录',
    errorNeedPromptOrImage: '请输入提示词或上传图片',
  },
  ltxVideo: {
    title: 'LTX 视频生成',
    description: '使用多帧引导生成视频。上传首帧（必需），可选添加尾帧和最多3个中间帧以实现精确控制。',
    firstFrame: '首帧',
    lastFrame: '尾帧（可选）',
    middleFrames: '中间帧（可选）',
    middleFrame1: '中间帧 1',
    middleFrame2: '中间帧 2',
    middleFrame3: '中间帧 3',
    dropImage: '将图片拖放到这里',
    dragDropOrClick: '拖放图片到这里，或点击选择',
    supportedFormats: '支持的格式：PNG、JPG、JPEG、WebP',
    prompt: '提示词',
    promptPlaceholder: '描述视频中发生的事情...',
    settings: '设置',
    model: '模型',
    duration: '时长',
    resolution: '分辨率',
    priority: '优先级',
    higherQuality: '使用更多步骤提高质量',
    generate: '生成视频',
    generating: '正在生成视频...',
    generatingInfo: '可在历史记录中查看结果，可安全离开此页面。',
    estimatedTime: '预计时间：约 {time} 秒',
    timeRemaining: '剩余时间：约 {time} 秒',
    generationComplete: '视频生成完成！',
    downloadVideo: '下载视频',
    newGeneration: '新建生成',
    errorFirstFrameAndPrompt: '请上传首帧并输入提示词',
    generateNeedFirstFrame: '生成视频（需要首帧）',
    generateNeedPrompt: '生成视频（需要提示词）',
    generateNeedBoth: '生成视频（需要首帧和提示词）',
  },
  imageEdit: {
    title: '图像编辑',
    model: '模型：',
    width: '宽度',
    height: '高度',
    nsfw: 'NSFW',
    describeEdit: '描述如何编辑图像...',
    jobInProgress: '任务进行中...',
    pressEnterToRegenerate: '按 Enter 键使用相同设置重新生成',
    pressEnterToGenerate: '按 Enter 键生成，Shift+Enter 换行',
    generationFailed: '生成失败',
    queued: '排队中',
    processing: '处理中...',
    setInputsAndGenerate: '设置输入并生成编辑后的图像',
    viewApiExamples: '查看 API 示例',
    startNew: '开始新的',
    new: '新建',
    image1Primary: '图像 1（主要）',
    image2Optional: '图像 2（可选）',
    image3Optional: '图像 3（可选）',
    upload: '上传',
    orPasteUrl: '或粘贴 URL',
    pleaseUploadValidImage: '请上传有效的图像文件',
    pleaseEnterPrompt: '请输入提示词',
    failedToSubmit: '提交图像编辑任务失败',
    justNow: '刚刚',
    minutesAgo: '{minutes}分钟前',
    hoursAgo: '{hours}小时前',
    daysAgo: '{days}天前',
  },
  faceSwap: {
    title: '换脸',
    uploadInstructions: '上传基础图像和至少一张参考面孔图像。参考图像中的面孔将被换入基础图像中。',
    image1BaseBody: '基础图像（身体）',
    image2ReferenceFace1: '参考面孔 1',
    image3ReferenceFace2: '参考面孔 2（可选）',
    image4ReferenceFace3: '参考面孔 3（可选）',
    upload: '上传',
    orPasteUrl: '或粘贴 URL',
    referenceFaceHint: '提示：请使用清晰的正脸照片，确保面部较大且居中，以获得最佳效果。',
    addExtraFaces: '添加更多参考面孔（可选）',
    hideExtraFaces: '收起额外参考面孔',
    generate: '生成换脸',
    regenerate: '重新生成',
    jobInProgress: '处理中...',
    generationFailed: '生成失败',
    queued: '排队中',
    processing: '处理中...',
    estimatedTime: '预计时间：约 2 分钟',
    uploadImagesAndGenerate: '上传基础图像和参考面孔以生成',
    new: '新建',
    pleaseUploadValidImage: '请上传有效的图像文件',
    pleaseUploadBaseImage: '请上传或提供基础图像的 URL',
    pleaseUploadReferenceFace: '请上传或提供至少一张参考面孔图像',
    failedToSubmit: '提交换脸任务失败',
  },
  videoGeneration: {
    title: '视频生成',
    model: '模型：',
    duration: '时长',
    seconds: '秒',
    uploadImageOptional: '上传图像（可选）',
    clickToSelectOrDrag: '点击选择或拖放',
    describeVideo: '描述您想要生成的视频...',
    generationInProgress: '生成进行中...',
    pressEnterToRegenerate: '按 Enter 键使用相同设置重新生成',
    pressEnterToGenerate: '按 Enter 键生成，Shift+Enter 换行',
    viewApiExamples: '查看 API 示例',
    startNewGeneration: '开始新生成',
    queued: '排队中',
    generatingVideo: '正在生成视频',
    justABitLonger: '再等一会儿...',
    somethingWentWrong: '出现问题。请重试。',
    videoPlaybackError: '视频播放错误',
    selectFromHistoryOrStart: '从历史记录中选择生成或开始新的',
    regenerateWithSettings: '使用相同设置重新生成',
    delete: '删除',
    pleaseUploadValidImage: '请上传有效的图像文件',
    pleaseEnterPrompt: '请输入提示词',
    failedToGenerate: '生成视频失败',
    videoExpired: '此视频不再可用（URL 已过期）',
    failedToDownload: '下载视频失败',
    justNow: '刚刚',
    minutesAgo: '{minutes}分钟前',
    hoursAgo: '{hours}小时前',
    daysAgo: '{days}天前',
  },
  virtualInfluencer: {
    title: '虚拟网红工作流',
    step1: '1. 语音生成',
    step2: '2. 视频预览',
    step3: '3. 升级至 1080p',
    step4: '4. 高帧率',
  },
  videoAddSound: {
    title: '视频添加音效',
    description: '为现有视频添加 AI 生成的音效和音乐。上传您的视频，描述所需的音频，让 AI 创作完美的配乐。',
    inputVideo: '输入视频',
    dropVideo: '将视频拖放到此处',
    dragDropOrClick: '将视频拖放到此处，或点击选择',
    supportedFormats: '支持的格式：MP4、MOV、WebM、AVI、MKV',
    prompt: '音效提示词',
    promptPlaceholder: '描述您想要添加的声音，例如"碎石路上的脚步声、鸟鸣声、微风声"...',
    generate: '为视频添加音效',
    generating: '正在生成音效...',
    generatingInfo: '可在历史记录中查看结果，可安全离开此页面。',
    estimatedTime: '预计时间：约 {time} 秒',
    timeRemaining: '剩余时间：约 {time} 秒',
    generationComplete: '音效添加成功！',
    downloadVideo: '下载带音效的视频',
    newGeneration: '新建生成',
    errorVideoAndPrompt: '请上传视频并输入音效提示词',
    generateNeedVideo: '添加音效（需要视频）',
    generateNeedPrompt: '添加音效（需要提示词）',
    generateNeedBoth: '添加音效（需要视频和提示词）',
  },
  ltxVideoExtend: {
    title: 'LTX 视频续写',
    description: '无缝延续现有视频。上传视频、描述接下来应发生的内容，LTX-2.3 模型会生成与输入风格一致的新画面和音频。',
    inputVideo: '输入视频',
    dropVideo: '将视频拖放到此处',
    dragDropOrClick: '将视频拖放到此处，或点击选择',
    supportedFormats: '支持的格式：MP4、MOV、WebM、AVI、MKV',
    prompt: '续写提示词',
    promptPlaceholder: '描述续写部分应发生的内容，例如"她继续说话，向镜头比划手势"...',
    durationLabel: '续写时长',
    resolutionLabel: '分辨率',
    modelLabel: '模型等级',
    modelStandard: '标准版',
    modelPro: '专业版',
    modelPremium: '旗舰版',
    generate: '续写视频',
    generating: '正在续写视频...',
    generatingInfo: '可在历史记录中查看结果，可安全离开此页面。',
    estimatedTime: '预计时间：约 {time} 秒',
    timeRemaining: '剩余时间：约 {time} 秒',
    generationComplete: '视频续写成功！',
    downloadVideo: '下载续写视频',
    newGeneration: '新建生成',
    errorVideoAndPrompt: '请上传视频并输入续写提示词',
    generateNeedVideo: '续写视频（需要视频）',
    generateNeedPrompt: '续写视频（需要提示词）',
    generateNeedBoth: '续写视频（需要视频和提示词）',
  },
};
